/**
 * Client-side HR / training summary diagnostics.
 *
 * Enable (any one):
 *   localStorage.setItem('SMARTTT_TRAINING_HR_DEBUG', '1'); location.reload();
 *   Same with: 'true', 'yes', 'on' (case-insensitive). Alias key: SMARTTT_HR_DEBUG
 *   Append to URL: ?hrDebug=1  or  ?trainingHrDebug=1
 *   .env: REACT_APP_SMARTTT_TRAINING_HR_DEBUG=1  (rebuild after change)
 *   window.__SMARTTT_TRAINING_HR_DEBUG__ = true
 *
 * Development: logging defaults ON unless you disable:
 *   localStorage.setItem('SMARTTT_TRAINING_HR_DEBUG', '0'); location.reload();
 *
 * Disable in production: remove flag / use 0 / false / off in storage.
 */

const STORAGE_KEYS = ["SMARTTT_TRAINING_HR_DEBUG", "SMARTTT_HR_DEBUG"];

function parseTriState(raw) {
  if (raw == null) return null;
  const s = String(raw).trim().toLowerCase();
  if (["1", "true", "yes", "on"].includes(s)) return true;
  if (["0", "false", "no", "off"].includes(s)) return false;
  return null;
}

function storageTriState(getter) {
  for (const key of STORAGE_KEYS) {
    const v = parseTriState(getter(key));
    if (v !== null) return v;
  }
  return null;
}

function urlDebugEnabled() {
  try {
    const q = new URLSearchParams(window.location.search);
    const a = parseTriState(q.get("hrDebug"));
    if (a === true) return true;
    if (a === false) return false;
    const b = parseTriState(q.get("trainingHrDebug"));
    if (b === true) return true;
    if (b === false) return false;
  } catch {
    /* ignore */
  }
  return null;
}

function envFlagEnabled() {
  try {
    return parseTriState(process.env.REACT_APP_SMARTTT_TRAINING_HR_DEBUG) === true;
  } catch {
    return false;
  }
}

export function isTrainingHrDebugEnabled() {
  if (typeof window === "undefined") return false;
  try {
    if (window.__SMARTTT_TRAINING_HR_DEBUG__ === true) return true;
    if (window.__SMARTTT_TRAINING_HR_DEBUG__ === 1) return true;
    if (String(window.__SMARTTT_TRAINING_HR_DEBUG__).toLowerCase() === "true") {
      return true;
    }

    const fromUrl = urlDebugEnabled();
    if (fromUrl === true) return true;
    if (fromUrl === false) return false;

    const fromSession = storageTriState((k) => sessionStorage.getItem(k));
    if (fromSession === true) return true;
    if (fromSession === false) return false;

    const fromLocal = storageTriState((k) => localStorage.getItem(k));
    if (fromLocal === true) return true;
    if (fromLocal === false) return false;

    if (envFlagEnabled()) return true;

    // Local dev: on by default so logs show without remembering storage keys.
    if (process.env.NODE_ENV === "development") return true;

    return false;
  } catch {
    return false;
  }
}

export function trainingHrDebugLog(section, data) {
  if (!isTrainingHrDebugEnabled()) return;
  console.log(`[SmartTT Training HR] ${section}`, data);
}

/** One-line hint when opening HR screens in production without any flag set. */
let hrDebugHintShown = false;
export function trainingHrDebugHintOnce() {
  if (typeof window === "undefined") return;
  if (process.env.NODE_ENV === "development") return;
  if (isTrainingHrDebugEnabled()) return;
  if (hrDebugHintShown) return;
  hrDebugHintShown = true;
  console.info(
    "%c[SmartTT Training HR]%c Verbose HR logs are off. Enable: add %c?hrDebug=1%c to the URL, or %clocalStorage.setItem(\"SMARTTT_TRAINING_HR_DEBUG\",\"1\")%c then reload.",
    "font-weight:bold;color:#0877BC",
    "color:inherit",
    "font-family:monospace",
    "color:inherit",
    "font-family:monospace",
    "color:inherit"
  );
}

/** Compact snapshot of hrsummary payload (avoids logging huge arrays). */
export function summarizeHrPayloadForLog(body) {
  if (!body || typeof body !== "object") return { body: body ?? null };
  const readings = body.readings;
  const graph = body.hrGraph;
  const windows = Array.isArray(body.sequence_windows_used)
    ? body.sequence_windows_used
    : null;
  return {
    player_id: body.player_id,
    user_id: body.user_id,
    summary_duration_sec: body.summary_duration_sec,
    summary_duration_source: body.summary_duration_source,
    active_span_sec: body.active_span_sec,
    hr_active_window_sec: body.hr_active_window_sec,
    hr_filtered_by_sequence: body.hr_filtered_by_sequence,
    hr_missing_sequence_times: body.hr_missing_sequence_times,
    hr_missing_sequence_count: body.hr_missing_sequence_count,
    te_start_time: body.te_start_time,
    te_end_time: body.te_end_time,
    te_planned_sec: body.te_planned_sec,
    summary_window_start_at: body.summary_window_start_at,
    summary_window_end_at: body.summary_window_end_at,
    hr_range_start_at: body.hr_range_start_at,
    hr_range_end_at: body.hr_range_end_at,
    hr_range_start_sec: body.hr_range_start_sec,
    hr_range_end_sec: body.hr_range_end_sec,
    sample_count: body.sample_count,
    sample_count_raw: body.sample_count_raw,
    dedup_dropped: body.dedup_dropped,
    sequence_windows_used_count: windows ? windows.length : null,
    sequence_windows_used: windows,
    maxHR: body.maxHR,
    minHR: body.minHR,
    averageHR: body.averageHR,
    readingsLen: Array.isArray(readings) ? readings.length : null,
    readingsSample: Array.isArray(readings) ? readings.slice(0, 6) : null,
    readingsFirstKeys:
      Array.isArray(readings) && readings[0] && typeof readings[0] === "object"
        ? Object.keys(readings[0])
        : null,
    graphLen: Array.isArray(graph) ? graph.length : null,
    graphSample: Array.isArray(graph) ? graph.slice(0, 6) : null,
    extraKeys: Object.keys(body).filter(
      (k) => !["readings", "hrGraph", "sequence_windows_used"].includes(k)
    ),
  };
}
